import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { Downloadable, PressRelease, Project, TeamMember, getPressPageContent } from '@/services/PressService'
import { BrandLogo, getPressBrandLogos } from '@/services/PressService/PressService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PressView } from '@/views/PressView'

interface PressProps {
  brandLogos: BrandLogo[]
  brands: Project[]
  downloadables: Downloadable[]
  pressReleases: PressRelease[]
  teamMembers: TeamMember[]
}

const PressPage: NextPageWithLayout<PressProps> = ({
  brandLogos,
  brands,
  downloadables,
  pressReleases,
  teamMembers,
}) => {
  const url = useCanonicalUrl(paths.press.index)
  const title = 'Press | Angel'
  const description = 'Angel Press Room'
  const openGraph = {
    title,
    description,
    url,
    type: 'website',
  }
  return (
    <>
      <Seo canonical={url} openGraph={openGraph} title={title} description={description} />
      <PressView
        brandLogos={brandLogos}
        brands={brands}
        downloadables={downloadables}
        pressReleases={pressReleases}
        teamMembers={teamMembers}
      />
    </>
  )
}

PressPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticProps = getStaticPropsErrorHandler<PressProps>(
  { filePath: 'pages/[locale]/press/index.tsx' },
  async ({ federationClient, locale, preview }) => {
    const [{ brands, downloadables, pressReleases, teamMembers, brandLogos }, translations] = await Promise.all([
      getPressPageContent(preview, locale).then(async (content) => ({
        ...content,
        brandLogos: await getPressBrandLogos(federationClient, content.brands),
      })),
      loadTranslations(locale, ['common', 'account']),
    ])

    return {
      props: {
        brandLogos,
        brands,
        downloadables,
        pressReleases,
        teamMembers,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default PressPage
